import {
  OrganizationSummaryDto,
  OrganizationSummarySubscriptionDto,
} from "@intelligems/ig-types/dtos/organization-summaries";
import { StripeSubscriptionSummaryDto } from "@intelligems/ig-types/dtos/stripe-subscription-links";
import { openNewTab } from "@src/utils/openNewTab";

export const stripeDashboardHref = (path: string) => {
  const base =
    import.meta.env.VITE_STAGE === "prod"
      ? "https://dashboard.stripe.com"
      : "https://dashboard.stripe.com/test";

  return `${base}/${path}`;
};

export const openStripe = (stripeUrl: string) => {
  openNewTab(stripeDashboardHref(stripeUrl));
};

export const getStatusFromAdminOrg = (
  org: OrganizationSummaryDto | null | undefined
) => {
  if (!org) {
    return "cancelled";
  }

  const hasActive =
    org.stripeSubscriptionSummaries.some((s) => s.status === "active") ||
    org.organizationSubscriptions.some((os) => os.status === "active");

  if (hasActive) {
    return "active";
  }

  const hasTrial = org.stripeSubscriptionSummaries.some(
    (s) => s.status === "trialing"
  );
  if (hasTrial) {
    return "trialing";
  }

  const hasPending =
    org.stripeSubscriptionSummaries.some((s) => s.status === "pending") ||
    org.organizationSubscriptions.some((s) => s.status === "pending");

  if (hasPending) {
    return "pending";
  }

  const hasCancelled =
    org.stripeSubscriptionSummaries.some(
      (s) => s.status === "cancelled" || s.status === "canceled"
    ) || org.organizationSubscriptions.some((os) => os.status === "cancelled");

  if (hasCancelled) {
    return "cancelled";
  }

  return "cancelled";
};

export function getBillingProviderFromAdminOrg(
  org: OrganizationSummaryDto | null | undefined
) {
  if (!org) {
    return null;
  }

  const organizationSubscriptions = org.organizationSubscriptions.every(
    (os) => os.status === "cancelled"
  )
    ? []
    : org.organizationSubscriptions;

  const hasShopify =
    org.stripeSubscriptionSummaries.some(
      (s) => s.billingProvider === "shopify"
    ) ||
    organizationSubscriptions.some((os) => os.billingProvider === "shopify");
  const hasStripe =
    org.stripeSubscriptionSummaries.some(
      (s) => s.billingProvider === "stripe"
    ) ||
    organizationSubscriptions.some((os) => os.billingProvider === "stripe");

  if (hasShopify && hasStripe) {
    return "both";
  }
  if (hasStripe) {
    return "stripe";
  }
  if (hasShopify) {
    return "shopify";
  }
  return null;
}

export const CurrencyFormat = new Intl.NumberFormat("en-US", {
  currency: "USD",
  style: "currency",
});

export const DecimalFormat = new Intl.NumberFormat("en-US", {});

export const IntegerCurrencyFormat = new Intl.NumberFormat("en-US", {
  currency: "USD",
  style: "currency",
  maximumFractionDigits: 0,
});

export const getBillingCycleValue = (
  stripeSubscriptionSummaries: StripeSubscriptionSummaryDto[],
  organizationSubscriptions: OrganizationSummarySubscriptionDto[]
) => {
  const slHasAnnual = stripeSubscriptionSummaries.some(
    (s) => s.interval === "year"
  );
  const slHasQuarterly = stripeSubscriptionSummaries.some(
    (s) => s.interval === "month" && (s.intervalCount ?? 1) === 3
  );
  const slHasSixMonth = stripeSubscriptionSummaries.some(
    (s) => s.interval === "month" && (s.intervalCount ?? 1) === 6
  );
  const slHasMonthly = stripeSubscriptionSummaries.some(
    (s) => s.interval === "month" && (s.intervalCount ?? 1) === 1
  );

  const osHasMonthly = organizationSubscriptions.some(
    (os) => os.interval === "month"
  );
  const osHasAnnual = organizationSubscriptions.some(
    (os) => os.interval === "year"
  );

  const hasAnnual = slHasAnnual || osHasAnnual;
  const hasMonthly = slHasMonthly || osHasMonthly;
  const hasQuarterly = slHasQuarterly;
  const hasSixMonth = slHasSixMonth;

  const cadenceCount = [
    hasAnnual,
    hasMonthly,
    hasQuarterly,
    hasSixMonth,
  ].filter(Boolean).length;

  if (cadenceCount > 1) {
    return "Varies";
  }
  if (hasQuarterly) {
    return "Quarterly";
  }
  if (hasSixMonth) {
    return "6-Month";
  }
  if (hasMonthly) {
    return "Monthly";
  }
  if (hasAnnual) {
    return "Yearly";
  }
  return null;
};
